import { mdiAlertCircleOutline, mdiCalendarClockOutline, mdiCheckCircleOutline } from "@mdi/js"
import type {
  ContactOrderPlanningPreview,
  ContactOrderPlanningRecipientSummary,
} from "../../../../../../api/kehrblatt/contact-orders"
import type { PropertyCustomerRole } from "../../../../../../_shared/_logic/enums"
import {
  formatPropertyCustomerRole,
  normalizePropertyCustomerRole,
} from "../../../_logic/customer-roles"
import { formatPropertyUnitDisplayForProperty } from "../../../../../../_shared/_logic/unit-labels"
import { formatContactAddress } from "../../../_logic/contact-addresses"
import {
  buildContactTag,
  contactPointVerificationLabel,
  formatPropertyAddressLines,
  groupContactPointsByCustomerId,
  normalizeContactPointList,
  sortContactPointsByQuality,
} from "../../../_logic/contact-quality-tags"
import { buildPropertyObjectTagPresentation } from "../../../_logic/object-tags"
import { buildAppointmentTimeTagPresentation } from "../../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../_shared/_logic/communication-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
  tooltipTitle,
} from "../../../../../../_shared/_logic/rich-tooltips"
import { buildWorkOrderSummaryTagPresentation } from "../../../_logic/summary-tag"
import { buildLetterOrderTag } from "../../../_logic/letter-order-tag"
import type {
  ContactOrderChannelValue,
  ContactOrderDigitalChannel,
  ContactOrderRecipientTag,
  ContactOrderTagDefinition,
} from "../../../_logic/planning-selection-contract"

export type ContactOrderTagContext = {
  propertyById: Record<string, Property>
  customersById: Record<string, any>
  contactPointsByCustomerId: ReturnType<typeof groupContactPointsByCustomerId>
}

export const formatContactOrderDate = (value?: string | null) => {
  if (!value) return "—"

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return "—"

  return parsed.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
}

export const formatContactOrderTimeRange = (start?: string | null, end?: string | null) => {
  if (!start) return "—"

  const startDate = new Date(start)
  const endDate = end ? new Date(end) : null

  if (Number.isNaN(startDate.getTime())) return "—"

  const startTime = startDate.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })

  if (!endDate || Number.isNaN(endDate.getTime())) return startTime

  const endTime = endDate.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })

  return `${startTime} – ${endTime}`
}

export const normalizeContactOrderText = (value: unknown) => `${value ?? ""}`.trim()

export const truncateRecipientLabel = (value: string, maxLength = 12) => {
  const text = normalizeContactOrderText(value)

  if (text.length <= maxLength) return text

  return `${text.slice(0, maxLength).trimEnd()}...`
}

const recipientLabel = (recipient: any) =>
  normalizeContactOrderText(recipient?.display_name) ||
  normalizeContactOrderText(recipient?.full_name) ||
  normalizeContactOrderText(recipient?.name) ||
  "Unbekannter Ansprechpartner"

const CONTACT_POINT_KIND_CHANNELS: Record<string, ContactOrderDigitalChannel[]> = {
  EMAIL: ["EMAIL"],
}
const RESIDENTIAL_CONTACT_ROLE_SEQUENCE: readonly PropertyCustomerRole[] = [
  "TENANT",
  "OWNER",
  "OTHER",
  "MANAGER",
]
const BUILDING_CONTACT_ROLE_SEQUENCE: readonly PropertyCustomerRole[] = [
  "MANAGER",
  "OWNER",
  "OTHER",
  "TENANT",
]

const contactPointValue = (point: any) => {
  const raw =
    normalizeContactOrderText(point?.normalized_value) || normalizeContactOrderText(point?.value)

  if (!raw) return ""

  return normalizeContactOrderText(point?.kind).toUpperCase() === "ADDRESS"
    ? formatContactAddress(raw)
    : raw
}

const contactPointId = (point: any) => {
  const value = normalizeContactOrderText(point?.id)

  return value || null
}

const uniqueContactOrderLabels = (labels: Array<string | null | undefined>) =>
  Array.from(new Set(labels.map(normalizeContactOrderText).filter(Boolean)))

const contactPointLifecycleStatus = (point: any) =>
  (normalizeContactOrderText(point?.lifecycle_status) || "ACTIVE").toUpperCase()

const isUsableContactPoint = (point: any) =>
  !["CANDIDATE", "STALE_CANDIDATE", "INVALID", "DO_NOT_USE", "ARCHIVED"].includes(
    contactPointLifecycleStatus(point),
  )

const verifiedContactPointValue = (point: any) => {
  const value = contactPointValue(point)

  return value ? `${value} (${contactPointVerificationLabel(point)})` : ""
}

const verifiedContactPointValuesForKind = (
  contactPoints: any[],
  kind: "EMAIL" | "MOBILE" | "FIXED_LINE" | "ADDRESS",
) =>
  sortContactPointsByQuality(normalizeContactPointList(contactPoints))
    .filter(
      (point) =>
        normalizeContactOrderText(point?.kind).toUpperCase() === kind &&
        isUsableContactPoint(point),
    )
    .map(verifiedContactPointValue)
    .filter(Boolean)

const contactWaySubLabel = (contactPoints: any[]) => {
  const activePoints = normalizeContactPointList(contactPoints).filter(
    (point) => isUsableContactPoint(point) && Boolean(contactPointValue(point)),
  )
  const hasKind = (kind: "EMAIL" | "MOBILE" | "FIXED_LINE" | "ADDRESS") =>
    activePoints.some((point) => normalizeContactOrderText(point?.kind).toUpperCase() === kind)
  const labels = [
    hasKind("EMAIL") ? "E-Mail" : null,
    hasKind("MOBILE") ? "Mobil" : null,
    hasKind("FIXED_LINE") ? "Festnetz" : null,
    hasKind("ADDRESS") ? "Adresse" : null,
  ].filter(Boolean)

  return labels.length ? labels.join(", ") : "Keine Kontaktdaten"
}

const buildContactOrderRecipientTooltip = ({
  nameLabel,
  unitLabel,
  contactPoints,
}: {
  title: string
  nameLabel: string
  roleLabel?: string | null
  unitLabel?: string | null
  contactPoints: any[]
  contactReasonLabel?: string | null
  sequenceLabel?: string | null
}) => {
  const emails = verifiedContactPointValuesForKind(contactPoints, "EMAIL")
  const mobiles = verifiedContactPointValuesForKind(contactPoints, "MOBILE")
  const fixedLines = verifiedContactPointValuesForKind(contactPoints, "FIXED_LINE")
  const phones = [...mobiles, ...fixedLines]

  return buildRichTooltip([
    tooltipTitle(nameLabel),
    tooltipField("Zuständig für", unitLabel),
    tooltipField("E-Mail", emails[0]),
    tooltipField("Telefon", phones[0]),
    !emails.length && !phones.length
      ? tooltipText("Bitte eine E-Mail-Adresse oder Telefonnummer ergänzen.")
      : null,
  ])
}

const buildRecipientChannelValues = ({
  customerId,
  customerLabel,
  contactPoints,
}: {
  customerId: string
  customerLabel: string
  contactPoints: any[]
}): ContactOrderChannelValue[] =>
  sortContactPointsByQuality(normalizeContactPointList(contactPoints))
    .filter(isUsableContactPoint)
    .flatMap((point) => {
      const value = contactPointValue(point)

      if (!value) return []

      const kind = normalizeContactOrderText(point?.kind).toUpperCase()
      const channels =
        kind === "MOBILE"
          ? (["MOBILE_MESSAGE", "PHONE"] as ContactOrderDigitalChannel[])
          : kind === "FIXED_LINE"
            ? (["PHONE"] as ContactOrderDigitalChannel[])
            : (CONTACT_POINT_KIND_CHANNELS[kind] ?? [])

      return channels.map((channel) => ({
        channel,
        customerId,
        customerLabel,
        value,
        contactPointId: contactPointId(point),
        isVerified: Boolean(point?.is_verified),
      }))
    })

export const unitLabelForOrder = (order?: WorkOrder | null, property?: Property | null) => {
  if (!order?.property_unit_id) return "Liegenschaft"

  return (
    formatPropertyUnitDisplayForProperty(
      property,
      order.property_unit_id,
      order.property_unit_label,
    ) ?? "Wohneinheit"
  )
}

export const unitScopeForOrder = (order?: WorkOrder | null, property?: Property | null) => {
  const unit = (property?.units ?? []).find(
    (entry) => `${entry.id}` === `${order?.property_unit_id ?? ""}`,
  )

  if (!unit) return "BUILDING"

  const unitType = normalizeContactOrderText(unit.unit_type).toUpperCase()

  if (unitType === "RESIDENTIAL") return "RESIDENTIAL"

  const hasResidentialUnits = (property?.units ?? []).some(
    (entry) => normalizeContactOrderText(entry.unit_type).toUpperCase() === "RESIDENTIAL",
  )

  return unitType === "BUILDING" && !hasResidentialUnits ? "RESIDENTIAL" : "BUILDING"
}

const contactRoleSequenceForScope = (scope: string) =>
  scope === "RESIDENTIAL" ? RESIDENTIAL_CONTACT_ROLE_SEQUENCE : BUILDING_CONTACT_ROLE_SEQUENCE

const sortRecipientsByContactRoleSequence = (
  recipients: any[],
  order: WorkOrder | null | undefined,
  property: Property | null | undefined,
) => {
  const roleSequence = contactRoleSequenceForScope(unitScopeForOrder(order, property))
  const roleRank = new Map(roleSequence.map((role, index) => [role, index]))

  return recipients
    .map((recipient, index) => ({ recipient, index }))
    .sort((left, right) => {
      const leftRole = normalizePropertyCustomerRole(left.recipient?.role)
      const rightRole = normalizePropertyCustomerRole(right.recipient?.role)
      const leftRank = roleRank.get(leftRole) ?? roleSequence.length
      const rightRank = roleRank.get(rightRole) ?? roleSequence.length

      return leftRank - rightRank || left.index - right.index
    })
    .map((entry) => entry.recipient)
}

export const buildContactOrderAddressLines = (
  order: WorkOrder | null | undefined,
  propertyById: Record<string, Property>,
) => {
  const property = order?.property_id ? propertyById[order.property_id] : null

  if (property) return formatPropertyAddressLines(property, order?.property_unit_id ?? null)

  const fallback = normalizeContactOrderText(order?.property_unit_label)

  return fallback ? [`Wohneinheit: ${fallback}`] : ["Unbekannte Liegenschaft"]
}

export const buildContactOrderSummaryTag = (
  order: WorkOrder | null,
  property: Property | null | undefined,
  event: ScheduleEvent,
  employeeLabel: string,
  isDirty: boolean,
) => {
  const summary = buildWorkOrderSummaryTagPresentation({ workOrder: order ?? null })
  const appointment = buildContactOrderAppointmentTag(event, employeeLabel)
  const start = new Date(event.start_at)
  const dateOptions = { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit" } as const
  const numberLabel = Number.isNaN(start.getTime())
    ? "—"
    : start.toLocaleDateString("de-DE", dateOptions)
  const fullDate = Number.isNaN(start.getTime())
    ? ""
    : start.toLocaleDateString("de-DE", { ...dateOptions, year: "numeric" })
  const tag = buildLetterOrderTag({
    property_address: property,
    property_unit_type: property?.units?.find((unit) => unit.id === order?.property_unit_id)
      ?.unit_type,
    unit_label: order?.property_unit_label,
    order_type_internal_label: summary.label,
  })

  return {
    ...tag,
    numberLabel,
    color: summary.color,
    tooltip: buildRichTooltip([
      tooltipTitle(`Auftrag ${summary.numberLabel}`),
      property
        ? tooltipField(
            "Liegenschaft",
            formatPropertyAddressLines(property, order?.property_unit_id ?? null).join(", "),
          )
        : null,
      tooltipField("Auftragstyp", summary.fullLabel),
      tooltipField("Einheit", unitLabelForOrder(order, property)),
      tooltipField("Frist", summary.dueLabel),
      tooltipField("Termin", fullDate),
      tooltipField("Zeitfenster", appointment.label),
      tooltipField("Mitarbeiter", employeeLabel),
      isDirty ? tooltipText("Die Terminangaben wurden in dieser Planung geändert.") : null,
      appointment.tooltip,
    ]),
    tooltipHtml: true,
  }
}

export const buildContactOrderPropertyTag = (
  order: WorkOrder | null | undefined,
  property: Property | null | undefined,
  propertyById: Record<string, Property>,
) => {
  const addressLines = buildContactOrderAddressLines(order, propertyById)
  const tag = buildPropertyObjectTagPresentation({
    property,
    propertyUnitId: order?.property_unit_id ?? null,
    fallbackAddressLabel: addressLines[0] ?? null,
    fallbackUnitLabel: order?.property_unit_label ?? null,
    mode: "address-first",
  })

  return {
    ...tag,
    tooltip: buildRichTooltip([
      tooltipTitle("Liegenschaft"),
      ...addressLines.map((line) => tooltipText(line)),
    ]),
  }
}

export const buildContactOrderAppointmentTag = (
  event: ScheduleEvent,
  employeeLabel: string,
): ContactOrderTagDefinition => {
  const timeTag = buildAppointmentTimeTagPresentation(event)
  const communication = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
  })

  return {
    label: timeTag.label,
    subLabel: [communication.label, employeeLabel].filter(Boolean).join(" · "),
    color: communication.definition.color,
    icon: mdiCalendarClockOutline,
    tooltip:
      communication.tooltip +
      buildRichTooltip([
        timeTag.usesCustomerWindow
          ? tooltipField("Kalenderzeit", timeTag.scheduledPeriodLabel)
          : null,
        timeTag.tooltipDescription ? tooltipText(timeTag.tooltipDescription) : null,
      ]),
    tooltipHtml: true,
  }
}

export const buildRecipientsForContactOrder = (
  order: WorkOrder | null | undefined,
  context: ContactOrderTagContext,
): ContactOrderRecipientTag[] => {
  const property = order?.property_id ? context.propertyById[order.property_id] : null
  const seen = new Set<string>()

  return sortRecipientsByContactRoleSequence(order?.recipients ?? [], order, property)
    .map((recipient: any, index: number) => {
      const customerId = normalizeContactOrderText(recipient?.customer_id)

      if (!customerId || seen.has(customerId)) return null

      seen.add(customerId)
      const roleLabel = formatPropertyCustomerRole(normalizePropertyCustomerRole(recipient?.role))
      const contactPoints = context.contactPointsByCustomerId[customerId] ?? []
      const unitLabel = unitLabelForOrder(order, property)
      const sequenceLabel = `${index + 1}. Ansprechpartner in der bestehenden Kontakt-Reihenfolge`
      const tag = buildContactTag({
        id: customerId || `${order?.id ?? "order"}-recipient-${index}`,
        customer: context.customersById[customerId] ?? null,
        fallbackLabel: recipientLabel(recipient),
        contactPoints,
        unitLabel,
        roleLabel,
        showRoleInLabel: false,
        contactReasonLabel: "Terminankündigung für diesen Auftrag",
        sequenceLabel,
      })
      const nameLabel = normalizeContactOrderText(tag.nameLabel) || recipientLabel(recipient)
      const normalizedRoleLabel =
        normalizeContactOrderText(tag.roleLabel) || roleLabel || "Nicht hinterlegt"

      return {
        customerId,
        nameLabel,
        roleLabel,
        tag: {
          ...tag,
          subLabel: contactWaySubLabel(contactPoints),
          nameLabel,
          roleLabel: normalizedRoleLabel,
          tooltip: buildContactOrderRecipientTooltip({
            title: roleLabel ? `${roleLabel}: ${nameLabel}` : nameLabel,
            nameLabel,
            roleLabel: normalizedRoleLabel,
            unitLabel,
            contactPoints,
            contactReasonLabel: "Terminankündigung für diesen Auftrag",
            sequenceLabel,
          }),
        },
        channelValues: buildRecipientChannelValues({
          customerId,
          customerLabel: nameLabel,
          contactPoints,
        }),
      }
    })
    .filter(Boolean) as ContactOrderRecipientTag[]
}

export const buildRecipientSummaryTag = (
  summary: ContactOrderPlanningRecipientSummary,
): ContactOrderRecipientTag | null => {
  const customerId = normalizeContactOrderText(summary.customer_id)

  if (!customerId) return null

  const nameLabel = normalizeContactOrderText(summary.label) || "Ansprechpartner"
  const roleLabel = normalizeContactOrderText(summary.role_label)
  const channelLabel = (summary.channel_labels ?? [])
    .map(normalizeContactOrderText)
    .filter(Boolean)
    .join(", ")

  return {
    customerId,
    nameLabel,
    roleLabel,
    channelValues: [],
    tag: {
      label: roleLabel
        ? `${roleLabel} (${truncateRecipientLabel(nameLabel)})`
        : truncateRecipientLabel(nameLabel, 20),
      subLabel: channelLabel || "Keine Kontaktdaten",
      color: summary.color || "rose",
      icon: summary.channel_labels?.length ? mdiCheckCircleOutline : mdiAlertCircleOutline,
      tooltip: buildRichTooltip([
        tooltipTitle("Ansprechpartner"),
        tooltipField("Name", nameLabel),
        roleLabel ? tooltipField("Rolle", roleLabel) : null,
        channelLabel
          ? tooltipField("Kontaktwege", channelLabel)
          : tooltipText("Keine nutzbaren Kontaktwege."),
        normalizeContactOrderText(summary.tooltip) ? tooltipText(summary.tooltip) : null,
      ]),
      tooltipHtml: true,
      nameLabel,
      roleLabel,
    },
  }
}

export const buildRecipientsFromPlanningPreview = (
  preview: ContactOrderPlanningPreview | null,
): ContactOrderRecipientTag[] =>
  (preview?.recipient_summaries ?? [])
    .map(buildRecipientSummaryTag)
    .filter(Boolean) as ContactOrderRecipientTag[]
